import { z } from "zod";
import { preferredAppTarget, type ChurchSignInAddresses } from "@how/tenancy";

/*
 * The public Shop (ADR 0071): the church website lists what the church sells, at /shop and in a store collection block.
 * Since ADR 0076 each product's Buy opens its own page on the website (`/shop/<id>`), where a guest pays by card without
 * an account; a member may still sign in and buy in the member portal's Shop. Nothing here reads the database: `get_public_shop` and the page envelope's `products` part do
 * (supabase/migrations/20261023120000_public_shop.sql). A visitor gets a product's name, description, picture, its
 * options' names and prices and whether each is in stock; never a count.
 */

const productImageSchema = z.object({
  path: z.string().min(1),
  alt: z.string().optional(),
});

export const publicProductSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  currency: z.string().regex(/^[a-z]{3}$/),
  /** How it reaches a buyer (ADR 0047 step 2), for the guest's purchase form (ADR 0076); "either" when not said. */
  fulfilment: z.enum(["pickup", "ship", "either"]).optional(),
  image: productImageSchema.optional(),
  variants: z
    .array(
      z.object({
        /** The option's id, which a guest's purchase form sends back (ADR 0076); absent from an older envelope. */
        id: z.string().min(1).optional(),
        name: z.string().min(1),
        priceCents: z.number().int().nonnegative(),
        available: z.boolean(),
      }),
    )
    .min(1),
});

export type PublicProduct = z.infer<typeof publicProductSchema>;

/** Where the Buy link goes: the database's answer, the shape `church_sign_in_addresses` gives a signed-in member. */
const memberSignInSchema = z.object({
  dash: z.null().default(null),
  members: z.string().min(1).nullable().default(null),
  preferred: z.enum(["how", "church"]).default("how"),
});

/** A church with no address of its own, and what the link uses when the answer cannot be read (ADR 0067 A6). */
const HOW_ADDRESS_ONLY: ChurchSignInAddresses = { dash: null, members: null, preferred: "how" };

/** The products part of an envelope, one product at a time (one that fails its contract is left out). */
export function parsePublicProducts(items: readonly unknown[]): PublicProduct[] {
  const kept: PublicProduct[] = [];
  for (const item of items) {
    const parsed = publicProductSchema.safeParse(item);
    if (parsed.success) kept.push(parsed.data);
  }
  return kept;
}

/** The envelope's `memberSignIn`, or HoW's own address when it is missing or does not parse. */
export function parseMemberSignIn(raw: unknown): ChurchSignInAddresses {
  const parsed = memberSignInSchema.safeParse(raw);
  return parsed.success ? parsed.data : HOW_ADDRESS_ONLY;
}

/** The address of the website's Shop page. */
export const shopPaths = { shop: "/shop" } as const;

/** Whether a request path (as `requestPath` gives it) is the Shop page, or one product's page in it (ADR 0076). */
export function isShopPath(path: string): boolean {
  return path === "shop" || shopProductIdOf(path) !== null;
}

const PRODUCT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The product a request path names (`shop/<id>`, ADR 0076), or null for the Shop page itself or anything else. */
export function shopProductIdOf(path: string): string | null {
  const match = /^shop\/([^/]+)$/.exec(path);
  const id = match?.[1]?.toLowerCase() ?? "";
  return PRODUCT_ID.test(id) ? id : null;
}

/** A product's own page on the church website, where a guest buys it (ADR 0076). */
export function productPath(id: string): string {
  return `${shopPaths.shop}/${encodeURIComponent(id)}`;
}

/** The member portal's Shop, where Buy goes. The portal has no page per product, so every product links here. */
export const PORTAL_SHOP_PATH = "/shop";

/**
 * The member portal's Shop for this church, by the rule HoW's own links between the apps follow (ADR 0067 R9): the
 * church's own member address when it prefers it and it is live, otherwise HoW's (`<slug>.members.<base>`). Null for a
 * slug HoW would never have made, so a page never links somewhere made up.
 */
export function memberShopUrl(
  signIn: ChurchSignInAddresses,
  slug: string,
  baseDomain: string,
): string | null {
  return memberPortalUrl(signIn, slug, baseDomain, PORTAL_SHOP_PATH);
}

/** A page of the member portal for this church, by the same rule as `memberShopUrl` (ADR 0067 R9). */
export function memberPortalUrl(
  signIn: ChurchSignInAddresses,
  slug: string,
  baseDomain: string,
  path: string,
): string | null {
  try {
    const target = preferredAppTarget(signIn, slug, "member_portal", baseDomain, path);
    return `https://${target.host}${target.path}`;
  } catch {
    return null;
  }
}

/** An amount in the product's currency, as a visitor reads it: "$12.00". */
export function formatPrice(cents: number, currency: string, locale = "en-US"): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** A product's price: "$12.00" when every option costs the same, "From $12.00" when they differ. */
export function productPrice(product: Pick<PublicProduct, "variants" | "currency">): string {
  const prices = product.variants.map((v) => v.priceCents);
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const amount = formatPrice(low, product.currency);
  return low === high ? amount : `From ${amount}`;
}

/** Sold out: no option of it is in stock. */
export function isSoldOut(product: Pick<PublicProduct, "variants">): boolean {
  return product.variants.every((v) => !v.available);
}

/**
 * The options a visitor can choose from, in words: "Small, Medium, Large", with "(sold out)" after one that has run
 * out. Empty for a product that comes one way, so a lone "Standard" is never printed.
 */
export function productOptions(product: Pick<PublicProduct, "variants">): string {
  if (product.variants.length < 2) return "";
  return product.variants.map((v) => (v.available ? v.name : `${v.name} (sold out)`)).join(", ");
}

/** Everything a theme draws for one product (ADR 0071), worked out once so every theme shows the same facts. */
export interface ProductCard {
  readonly id: string;
  readonly name: string;
  readonly description: string | undefined;
  /** The picture's path in the church's media area and its description; undefined when the product has none. */
  readonly image: { readonly path: string; readonly alt: string } | undefined;
  /** "$12.00", or "From $20.00" when the options cost different amounts. */
  readonly price: string;
  readonly soldOut: boolean;
  /** "Small (sold out), Medium, XL"; empty for a product that comes one way. */
  readonly options: string;
  /**
   * Where Buy goes: the product's own page on the website, where a guest pays without an account (ADR 0076); null
   * when there is no Buy (sold out).
   */
  readonly buyHref: string | null;
  /** The member portal's Shop, for a member who would rather sign in; null when no address could be built. */
  readonly memberShopHref: string | null;
}

/**
 * A product as a card, for any theme's store collection block or Shop page. The standard theme's ProductGrid draws
 * these (`@how/theme-standard/components/ProductGrid.astro`, importable by another theme); a theme with its own look
 * draws the same cards its own way, so the price, Sold out and the Buy address never differ between themes.
 */
export function productCard(product: PublicProduct, shopUrl: string | null): ProductCard {
  const soldOut = isSoldOut(product);
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    image:
      product.image === undefined
        ? undefined
        : { path: product.image.path, alt: product.image.alt ?? "" },
    price: productPrice(product),
    soldOut,
    options: productOptions(product),
    buyHref: soldOut ? null : productPath(product.id),
    memberShopHref: shopUrl,
  };
}
